import { useEffect, useState } from "react";

import {
  InfoPanel,
  MobileMenu,
  Stepper,
  StepperProvider,
  useStepper,
} from "@king-ict/eupisi-ui/components";
import { useBreakpoint } from "@king-ict/eupisi-ui/hooks";
import { isEmpty } from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import qs from "qs";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useHistory,
  useLocation,
  useRouteMatch,
  useParams,
  Redirect,
} from "react-router";
import { v4 as uuidv4 } from "uuid";

import {
  getInstitutionDetails,
  getChildRequestCountByOib,
} from "../../api/kindergartenApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import { useBusinessRule } from "../../hooks/businessRule/useBusinessRule";
import ImportantDateEventEnum from "../../models/ImportantDate/ImportantDateEventEnum";
import ParentNumberEnum from "../../models/Person/ParentNumberEnum";
import ChildRequestsCount from "../../models/Request/ChildRequestsCount";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";
import { newRequestRoute, newPreschoolRequestRoute } from "../../routes/routes";
import { dashboard as dashboardRoute } from "../../routes/routes";
import {
  getInitialConversation,
  selectInitialConversationQuestionGroupNames,
} from "../../slices/questionnaireSlice";
import {
  getApplicantParentByRequestId,
  getApplicantParentByOib,
  getChildByRequestId,
  getChildByOib,
  getDraftRequest,
  getInstitutionByRequestId,
  getOtherParentByRequestId,
  getLocationProgramsByRequestId,
  getRequestCriterionDataByRequestId,
  getInitialConversationByRequestId,
  getRequestBasicInfo,
  setRequestUID,
  updateSelectedInstitution,
} from "../../slices/requestSlice";
import ApplicantParentDataStep from "./ApplicantParentDataStep/ApplicantParentDataStep";
import ChildDataStep from "./ChildDataStep/ChildDataStep";
import CriteriaStep from "./CriteriaStep/CriteriaStep";
import InitialConversationStep from "./InitialConversationStep/InitialConversationStep";
import InstitutionDataStep from "./InstitutionDataStep/InstitutionDataStep";
import InstitutionProgramDataStep from "./InstitutionProgramDataStep/InstitutionProgramDataStep";
import OtherParentDataStep from "./OtherParentDataStep/OtherParentDataStep";
import RequestInstitutionHeaderInfo from "./RequestInstitutionHeaderInfo";
import styles from "./RequestPage.module.scss";
import RequestReviewStep from "./RequestReviewStep/RequestReviewStep";
import RequestStepperActions from "./RequestStepperActions";
import RequestStepperHeader from "./RequestStepperHeader";
import { RequestStepperHeaderButtons } from "./RequestStepperHeaderButtons";

interface MatchParams {
  id: string;
}

export default function RequestPage() {
  const newRequestMatch = useRouteMatch<MatchParams>(newRequestRoute());
  const newPreschoolRequestMatch = useRouteMatch<MatchParams>(
    newPreschoolRequestRoute()
  );

  const location = useLocation();
  const history = useHistory();
  const params = qs.parse(location.search.substring(1));
  const { applicantParentOib, childOib } = params;
  const { requestId, guid } = useParams<{ requestId: string; guid: string }>();

  const user = useTypedSelector((state) => state.application.user);
  const newRequest = useTypedSelector((state) => state.request.request);
  const questionGroupNames = useSelector(
    selectInitialConversationQuestionGroupNames
  );
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const [openMenu, setOpenMenu] = useState(false);

  const [childrenRequestsCount, setChildrenRequestsCount] = useState(
    {} as ChildRequestsCount
  );

  const handleOpenMenu = () => {
    setOpenMenu(true);
  };

  const handleCloseMenu = () => {
    setOpenMenu(false);
  };

  const {
    isRequestCreationAllowed,
    isRequestEditingAllowed,
    isMaximumRequestCountReached,
  } = useBusinessRule();

  const dispatch = useAppDispatch();

  // TODO: Find better way of resolving peer dependencies for redirect case
  useEffect(() => {
    // New
    if (
      (applicantParentOib || user?.oib) &&
      childOib &&
      !isEmpty(pedagogicalYear.currentImportantDate) &&
      !isEmpty(pedagogicalYear.currentPedagogicalYear)
    ) {
      if (!isRequestCreationAllowed()) {
        history.push(dashboardRoute());
      } else {
        const uid = uuidv4();
        dispatch(setRequestUID(uid));
        dispatch(
          getApplicantParentByOib(applicantParentOib?.toString() ?? user.oib)
        );
        dispatch(getChildByOib(childOib as string));
        dispatch(getInitialConversation());
        getChildRequestCountByOib(
          childOib.toString(),
          applicantParentOib?.toString() ?? user.oib
        ).then((res) => {
          setChildrenRequestsCount(res);
        });
        if (user.currentPedagogicalYearInstitutionId) {
          getInstitutionDetails(+user.currentPedagogicalYearInstitutionId).then(
            (res) => {
              dispatch(updateSelectedInstitution(res));
            }
          );
        }
      }
    }
  }, [
    dispatch,
    user,
    childOib,
    history,
    isRequestCreationAllowed,
    applicantParentOib,
    pedagogicalYear.currentImportantDate,
    pedagogicalYear.currentPedagogicalYear,
    newRequest.child.dateOfBirth,
  ]);

  useEffect(() => {
    // Edit
    if (
      requestId &&
      !isEmpty(pedagogicalYear.currentImportantDate) &&
      !isEmpty(pedagogicalYear.currentPedagogicalYear)
    ) {
      (async () => {
        await dispatch(getRequestBasicInfo(+requestId))
          .then(unwrapResult)
          .then((item) => {
            if (!isRequestEditingAllowed(item)) {
              history.push(dashboardRoute());
            } else {
              if (item.parentNumberId === ParentNumberEnum.TwoParents) {
                dispatch(
                  getOtherParentByRequestId({
                    requestId: +requestId,
                    refreshData: true,
                  })
                );
              }

              dispatch(
                getApplicantParentByRequestId({
                  requestId: +requestId,
                  refreshData: true,
                })
              );
              dispatch(
                getChildByRequestId({
                  requestId: +requestId,
                  refreshData: true,
                })
              );
              dispatch(getInstitutionByRequestId(+requestId));
              dispatch(getLocationProgramsByRequestId(+requestId));
              dispatch(getRequestCriterionDataByRequestId(+requestId));

              dispatch(getInitialConversation());
              dispatch(getInitialConversationByRequestId(+requestId));
            }
          });
      })();
    }
  }, [
    dispatch,
    history,
    isRequestEditingAllowed,
    requestId,
    pedagogicalYear.currentImportantDate,
    pedagogicalYear.currentPedagogicalYear,
  ]);

  useEffect(() => {
    // Draft
    if (guid) {
      if (
        !isEmpty(pedagogicalYear.currentImportantDate) &&
        pedagogicalYear.currentImportantDate.eventId !==
          ImportantDateEventEnum.RequestCreation
      ) {
        history.push(dashboardRoute());
      } else if (!isEmpty(pedagogicalYear.currentImportantDate)) {
        dispatch(setRequestUID(guid));
        dispatch(getDraftRequest(guid));
        dispatch(getInitialConversation());
      }
    }
  }, [
    dispatch,
    guid,
    history,
    pedagogicalYear.currentImportantDate,
    pedagogicalYear.currentImportantDate.eventId,
  ]);

  const { t } = useTranslation();

  const steps = [
    {
      title: t("request.parentsStep"),
      substeps: [t("request.parentSubStepOne"), t("request.parentSubStepTwo")],
    },
    {
      title: t("request.childStep"),
      substeps: [],
    },
    {
      title: t("request.kindergartenStep"),
      substeps: [
        t("request.kindergartenSubStep"),
        t("request.locationAndProgramSubStep"),
      ],
    },
    {
      title: t("request.criteriaStep"),
      substeps: [],
    },
    {
      title: t("request.initialConversationStep"),
      substeps: questionGroupNames,
    },
    {
      title: t("request.confirmStep"),
      substeps: [],
    },
  ];

  const hasOneParent = () => {
    return (
      isEmpty(newRequest?.parentNumberId) ||
      newRequest.parentNumberId === ParentNumberEnum.OneParent
    );
  };

  const getSteps = () => {
    let newSteps = [...steps];
    if (hasOneParent()) {
      newSteps[0].substeps.splice(1, 1);
    }
    if (user.currentPedagogicalYearInstitutionId) {
      newSteps[2].substeps.splice(0, 1);
    }
    if (
      newRequest.selectedInstitution.usingQuestionnaire === undefined ||
      newRequest.selectedInstitution.usingQuestionnaire === false
    ) {
      newSteps = newSteps.filter(
        (step) => step.title !== t("request.initialConversationStep")
      );
    }
    return newSteps;
  };

  const [stepperState, stepperApi] = useStepper(getSteps());

  const sizeBreakpoint = useBreakpoint();

  function getStepContent(step: number, subStep?: number) {
    switch (step) {
      case 0:
        switch (subStep) {
          case 0:
            return <ApplicantParentDataStep />;
          case 1:
            return <OtherParentDataStep />;
        }
        break;
      case 1:
        return <ChildDataStep />;
      case 2:
        switch (subStep) {
          case 0:
            return user.currentPedagogicalYearInstitutionId ? (
              <InstitutionProgramDataStep />
            ) : (
              <InstitutionDataStep />
            );
          case 1:
            return <InstitutionProgramDataStep />;
        }
        break;
      case 3:
        return <CriteriaStep />;
      case 4:
        if (
          newRequest.selectedInstitution.usingQuestionnaire === undefined ||
          newRequest.selectedInstitution.usingQuestionnaire === false
        ) {
          return (
            <RequestReviewStep moveToSpecificStep={stepperApi.moveToSpecific} />
          );
        }
        return <InitialConversationStep subStep={subStep ?? 0} />;
      case 5:
        return (
          <RequestReviewStep moveToSpecificStep={stepperApi.moveToSpecific} />
        );
      default:
        return <InfoPanel message={"Unknown step"} color="danger" fullWidth />;
    }
  }

  return (
    newRequestMatch
      ? !isMaximumRequestCountReached(
          childrenRequestsCount.requestsCount,
          RequestTypeEnum.Kindergarten
        )
      : newPreschoolRequestMatch
      ? !isMaximumRequestCountReached(
          childrenRequestsCount.preschoolRequestsCount,
          RequestTypeEnum.Preschool
        )
      : true
  ) ? (
    <StepperProvider>
      <RequestStepperHeader
        currentStep={stepperState.currentStep}
        currentSubstep={stepperState.currentSubstep}
        openMobileMenu={handleOpenMenu}
      />
      <RequestInstitutionHeaderInfo {...stepperState} />

      <div className={styles.stepperWrapper}>
        {sizeBreakpoint === "xs" || sizeBreakpoint === "sm" ? (
          <Row>
            <MobileMenu open={openMenu} onClose={handleCloseMenu}>
              <div className={styles.mobileMenuButtons}>
                <RequestStepperHeaderButtons
                  currentStep={stepperState.currentStep}
                  currentSubstep={stepperState.currentSubstep}
                />
              </div>
              <aside>
                <Stepper data={getSteps()} {...stepperState} />
              </aside>
            </MobileMenu>
            <Col xs={12}>
              {getStepContent(
                stepperState.currentStep,
                stepperState.currentSubstep
              )}
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={3} className={styles.sidebar}>
              <aside>
                <Stepper data={getSteps()} {...stepperState} />
              </aside>
            </Col>
            <Col md={9} className={styles.content}>
              {getStepContent(
                stepperState.currentStep,
                stepperState.currentSubstep
              )}
            </Col>
          </Row>
        )}
      </div>

      <RequestStepperActions
        stepperState={stepperState}
        stepperApi={stepperApi}
      />
    </StepperProvider>
  ) : (
    <Redirect to="/page-not-available" />
  );
}
